import { supabase } from "./client";
import type { EdgeFunctionName, EdgeFunctionResponse } from "@nfp-tracker/utils";
import { captureException, trackEvent } from "../analytics/client";

type InvokeOptions = {
  body?: unknown;
  headers?: Record<string, string>;
  retryOnUnauthorized?: boolean;
};

class EdgeFunctionInvokeError extends Error {
  readonly functionName: string;
  readonly statusCode: number | null;
  readonly statusText: string | null;
  readonly responseError: string | null;
  readonly stage: "initial" | "retry";
  readonly originalName: string | null;

  constructor(options: {
    functionName: string;
    statusCode: number | null;
    statusText: string | null;
    responseError: string | null;
    stage: "initial" | "retry";
    originalName: string | null;
  }) {
    const statusLabel = options.statusCode != null
      ? `${options.statusCode}${options.statusText ? ` ${options.statusText}` : ""}`
      : "unknown status";
    const detail = options.responseError ? `: ${options.responseError}` : "";

    super(`Edge function "${options.functionName}" failed (${statusLabel})${detail}`);
    this.name = "EdgeFunctionInvokeError";
    this.functionName = options.functionName;
    this.statusCode = options.statusCode;
    this.statusText = options.statusText;
    this.responseError = options.responseError;
    this.stage = options.stage;
    this.originalName = options.originalName;
  }
}

const isUnauthorizedInvokeError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;

  const maybeError = error as {
    context?: { status?: number };
    message?: string;
  };

  if (maybeError.context?.status === 401) return true;
  return maybeError.message?.toLowerCase().includes("401") ?? false;
};

const getInvokeStatusCode = (error: unknown): number | null => {
  if (!error || typeof error !== "object") {
    return null;
  }

  const maybeError = error as { context?: { status?: number } };
  return typeof maybeError.context?.status === "number"
    ? maybeError.context.status
    : null;
};

const getInvokeStatusText = (error: unknown): string | null => {
  if (!error || typeof error !== "object") {
    return null;
  }

  const maybeError = error as { context?: { statusText?: string } };
  return typeof maybeError.context?.statusText === "string"
    ? maybeError.context.statusText
    : null;
};

const getInvokeContextResponse = (error: unknown): Response | null => {
  if (!error || typeof error !== "object") {
    return null;
  }

  const maybeError = error as { context?: unknown };
  return maybeError.context instanceof Response ? maybeError.context : null;
};

const toSingleLine = (value: string): string => value.replace(/\s+/g, " ").trim();

const trimErrorDetail = (value: string): string => toSingleLine(value).slice(0, 240);

const extractResponseErrorText = async (response: Response | null): Promise<string | null> => {
  if (!response) {
    return null;
  }

  try {
    const rawBody = await response.clone().text();
    if (!rawBody) {
      return null;
    }

    const trimmedBody = rawBody.trim();
    if (!trimmedBody) {
      return null;
    }

    try {
      const parsed = JSON.parse(trimmedBody) as Record<string, unknown>;
      const candidate = [parsed.error, parsed.code, parsed.details, parsed.hint]
        .find((entry) => typeof entry === "string" && entry.trim().length > 0);

      if (typeof candidate === "string") {
        return trimErrorDetail(candidate);
      }
    } catch {
      // Fall back to raw body when response is not JSON.
    }

    return trimErrorDetail(trimmedBody);
  } catch {
    return null;
  }
};

const enrichInvokeError = async <TName extends EdgeFunctionName>(
  error: unknown,
  functionName: TName,
  stage: "initial" | "retry"
): Promise<EdgeFunctionInvokeError> => {
  const statusCode = getInvokeStatusCode(error);
  const statusText = getInvokeStatusText(error);
  const responseError = await extractResponseErrorText(getInvokeContextResponse(error));
  const originalName =
    error && typeof error === "object" && "name" in error && typeof error.name === "string"
      ? error.name
      : null;

  return new EdgeFunctionInvokeError({
    functionName,
    statusCode,
    statusText,
    responseError,
    stage,
    originalName,
  });
};

export const getAccessToken = async (): Promise<string | null> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (session?.access_token) {
    return session.access_token;
  }

  const {
    data: { session: refreshed },
  } = await supabase.auth.refreshSession();

  return refreshed?.access_token ?? null;
};

export const invokeEdgeFunction = async <TName extends EdgeFunctionName>(
  functionName: TName,
  options: InvokeOptions = {}
): Promise<{ data: EdgeFunctionResponse<TName> | null; error: unknown | null }> => {
  const analyticsContext = {
    functionName,
    hasBody: options.body != null,
    retryOnUnauthorized: options.retryOnUnauthorized !== false,
  };
  const token = await getAccessToken();

  // Every edge function the client calls authenticates the caller itself. Without a session
  // token supabase-js falls back to the anon key as the bearer, which those functions can only
  // answer with 401 — a guaranteed round trip to a guaranteed failure, reported twice (initial
  // + retry) as an EdgeFunctionInvokeError. Fail locally instead, and keep it out of Sentry:
  // "not signed in yet" is a state, not an error.
  if (!token) {
    trackEvent("edge_function_skipped_unauthenticated", analyticsContext);
    return {
      data: null,
      error: new EdgeFunctionInvokeError({
        functionName,
        statusCode: 401,
        statusText: "Unauthorized",
        responseError: "No access token available for edge function call",
        stage: "initial",
        originalName: null,
      }),
    };
  }

  const headers = {
    ...(options.headers || {}),
    Authorization: `Bearer ${token}`,
  };

  const first = await supabase.functions.invoke<EdgeFunctionResponse<TName>>(functionName, {
    body: options.body,
    headers,
  });

  if (!first.error || options.retryOnUnauthorized === false) {
    if (first.error) {
      const enrichedError = await enrichInvokeError(first.error, functionName, "initial");
      trackEvent("edge_function_failed", {
        ...analyticsContext,
        stage: "initial",
        statusCode: enrichedError.statusCode,
      });
      captureException(enrichedError, {
        ...analyticsContext,
        source: "invokeEdgeFunction",
        stage: "initial",
        statusCode: enrichedError.statusCode,
        statusText: enrichedError.statusText,
        responseError: enrichedError.responseError,
        originalErrorName: enrichedError.originalName,
      });
      return { data: first.data, error: enrichedError };
    } else {
      trackEvent("edge_function_invoked", {
        ...analyticsContext,
        retried: false,
      });
    }

    return first;
  }

  if (!isUnauthorizedInvokeError(first.error)) {
    const enrichedError = await enrichInvokeError(first.error, functionName, "initial");
    trackEvent("edge_function_failed", {
      ...analyticsContext,
      stage: "initial",
      statusCode: enrichedError.statusCode,
    });
    captureException(enrichedError, {
      ...analyticsContext,
      source: "invokeEdgeFunction",
      stage: "initial",
      statusCode: enrichedError.statusCode,
      statusText: enrichedError.statusText,
      responseError: enrichedError.responseError,
      originalErrorName: enrichedError.originalName,
    });
    return { data: first.data, error: enrichedError };
  }

  const refreshedToken = await getAccessToken();

  // Same reasoning as the pre-flight check: if the refresh produced nothing, retrying with the
  // anon key just buys a second 401.
  if (!refreshedToken) {
    trackEvent("edge_function_skipped_unauthenticated", { ...analyticsContext, stage: "retry" });
    return {
      data: null,
      error: new EdgeFunctionInvokeError({
        functionName,
        statusCode: 401,
        statusText: "Unauthorized",
        responseError: "Session refresh produced no access token",
        stage: "retry",
        originalName: null,
      }),
    };
  }

  const retryHeaders = {
    ...(options.headers || {}),
    Authorization: `Bearer ${refreshedToken}`,
  };

  const retried = await supabase.functions.invoke<EdgeFunctionResponse<TName>>(functionName, {
    body: options.body,
    headers: retryHeaders,
  });

  if (retried.error) {
    const enrichedError = await enrichInvokeError(retried.error, functionName, "retry");
    trackEvent("edge_function_failed", {
      ...analyticsContext,
      stage: "retry",
      statusCode: enrichedError.statusCode,
    });
    captureException(enrichedError, {
      ...analyticsContext,
      source: "invokeEdgeFunction",
      stage: "retry",
      statusCode: enrichedError.statusCode,
      statusText: enrichedError.statusText,
      responseError: enrichedError.responseError,
      originalErrorName: enrichedError.originalName,
    });
    return { data: retried.data, error: enrichedError };
  } else {
    trackEvent("edge_function_invoked", {
      ...analyticsContext,
      retried: true,
    });
  }

  return retried;
};
